import { Button } from "@/components/ui/button";
import { Check, X } from "lucide-react";
import { Link } from "react-router-dom";

const FORMULES = [
  {
    name: "Économique",
    text: "Vous préparez une partie du déménagement, notre équipe s'occupe principalement du transport et de la manutention.",
    points: [
      "Protection du mobilier / literie",
      "Protection des écrans plasma et LCD",
      "Protection des objets hors carton (tableaux, miroirs, etc.)",
    ],
  },
  {
    name: "Économique Plus",
    text: "Une solution intermédiaire pour bénéficier d'une aide supplémentaire tout en maîtrisant votre budget.",
    intro: "En plus de la formule Économique :",
    points: [
      "Démontage du mobilier si nécessaire",
      "Remontage du mobilier",
    ],
  },
  {
    name: "Standard",
    text: "Une formule équilibrée avec prise en charge des éléments essentiels pour un déménagement confortable et organisé.",
    intro: "En plus de la formule Économique Plus :",
    points: [
      "Mise en carton de l'ensemble des articles fragiles",
      "Déballage des cartons fragiles",
    ],
    highlighted: true,
  },
  {
    name: "Luxe",
    text: "Une prestation complète pour déléguer l'emballage, la manutention et le transport à nos déménageurs.",
    intro: "En plus de la formule Standard :",
    points: [
      "Mise en carton de l'ensemble des articles non fragiles",
    ],
    excluded: [
      "Déballage des cartons non fragiles",
    ],
  },
];

export default function Formules({ heading = null, intro = null, outro = null }) {
  return (
    <section id="formules" data-testid="formules-section" className="py-10 sm:py-14 bg-white">
      <div className="bd-container">
        <div className="max-w-3xl">
          <span className="bd-eyebrow">Formules de déménagement</span>
          <h2 className="bd-h2 mt-5 text-[26px] sm:text-[30px] lg:text-[36px]">
            {heading || (
              <>
                Des formules adaptées à <span style={{ color: "#237cb5" }}>chaque déménagement</span>
              </>
            )}
          </h2>
          <p className="mt-5 text-[#3a4a5a] leading-relaxed">
            {intro ||
              "Big Déménagement propose plusieurs formules pour s'adapter à votre budget, votre disponibilité et le niveau d'accompagnement souhaité."}
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 items-stretch">
          {FORMULES.map((f, i) => (
            <article
              key={f.name}
              data-testid={`formule-card-${i}`}
              className={`relative rounded-2xl border p-6 transition-[transform,box-shadow,border-color,background-color,color] duration-300 flex flex-col ${
                f.highlighted
                  ? "border-transparent text-white shadow-[0_30px_60px_-30px_rgba(35,124,181,0.55)] lg:-translate-y-6"
                  : "border-[#e6edf3] bg-white hover:-translate-y-1 hover:border-[#5793d8]/60 hover:shadow-[0_20px_40px_-24px_rgba(35,124,181,0.35)]"
              }`}
              style={f.highlighted ? { background: "linear-gradient(135deg,#5793d8,#8fb8e3)" } : {}}
            >
              {f.highlighted && (
                <span className="absolute -top-3 left-6 inline-flex items-center rounded-full bg-white px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-[#237cb5] shadow-md">
                  Recommandée
                </span>
              )}
              <div className={`text-xs uppercase tracking-[0.18em] ${f.highlighted ? "text-white/90" : "text-[#5b6b7a]"}`}>Formule</div>
              <h3 className={`mt-1 text-2xl font-extrabold ${f.highlighted ? "text-white" : "text-[#0f1d2b]"}`}>{f.name}</h3>
              <p className={`mt-3 text-sm leading-relaxed ${f.highlighted ? "text-white" : "text-[#5b6b7a]"}`}>{f.text}</p>
              {f.intro && (
                <p className={`mt-4 text-xs font-semibold uppercase tracking-wider ${f.highlighted ? "text-white" : "text-[#237cb5]"}`}>
                  {f.intro}
                </p>
              )}
              <ul className={`mt-3 space-y-2 text-sm flex-1 ${f.highlighted ? "text-white" : "text-[#0f1d2b]"}`}>
                {f.points.map((p) => (
                  <li key={p} className="flex items-start gap-2">
                    <Check className={`h-4 w-4 mt-0.5 shrink-0 ${f.highlighted ? "text-white" : "text-[#237cb5]"}`} />
                    <span>{p}</span>
                  </li>
                ))}
                {f.excluded?.map((p) => (
                  <li key={p} className="flex items-start gap-2 opacity-70">
                    <X className={`h-4 w-4 mt-0.5 shrink-0 ${f.highlighted ? "text-white/70" : "text-[#b5455a]"}`} />
                    <span className="line-through">
                      {p}
                      <span className={`block not-italic no-underline text-[11px] font-semibold uppercase tracking-wider mt-0.5 ${f.highlighted ? "text-white/70" : "text-[#b5455a]"}`}>
                        Non inclus
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>

        {outro && (
          <p className="mt-8 max-w-3xl text-[15px] leading-[1.8] text-[#3a4a5a]">{outro}</p>
        )}

        <div className="mt-10 flex justify-center">
          <Button
            asChild
            data-testid="formules-cta"
            className="h-12 px-6 rounded-xl bg-[#237cb5] hover:bg-[#1c6699] text-white font-semibold shadow-[0_18px_30px_-12px_rgba(35,124,181,0.55)]"
          >
            <Link to="/formules">Découvrir nos formules</Link>
          </Button>
        </div>
      </div>
    </section>
  );
}
